"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { AccountSwitchedDialog } from "@/components/session/AccountSwitchedDialog";
import {
  getAccountSwitch,
  isAccountSwitchedError,
  isUnauthorizedError,
  pingSessionHeartbeat,
  subscribeToAccessTokenUpdates,
  subscribeToAccountSwitch,
  type AccessTokenUpdate,
  type AccountSwitchInfo,
} from "@/lib/api-client";
import { decodeJwtPayload, readJwtExpiry, readJwtIssuedAt } from "@/lib/jwt";
import { watchForCrossTabAccountSwitch } from "@/lib/session/account-switch";
import { SESSION_EXPIRY_BUFFER_MS } from "@/lib/session/cookie";
import { logoutAndRedirect, syncSessionCookie } from "@/lib/session/client";
import type { SessionProfile, SessionState } from "@/lib/session/types";
import { clearWorkspacePreferenceCache } from "@/lib/workspace-preferences";

const SESSION_STORAGE_KEY = "scirix.session_state";
const MIN_HEARTBEAT_INTERVAL_MS = 15 * 1000;
const FALLBACK_HEARTBEAT_INTERVAL_MS = 60 * 1000;
const REFRESH_RETRY_INTERVAL_MS = 30 * 1000;

function isStoredSessionExpired(state: SessionState): boolean {
  if (state.status !== "authenticated") {
    return false;
  }
  const expiresAt = Date.parse(state.expiresAt);
  if (Number.isNaN(expiresAt)) {
    return true;
  }
  return expiresAt - SESSION_EXPIRY_BUFFER_MS <= Date.now();
}

function readStoredSession(): SessionState {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      const state = JSON.parse(raw) as SessionState;
      if (isStoredSessionExpired(state)) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        return { status: "unauthenticated" };
      }
      return state;
    }
  } catch {
    // ignore
  }
  return { status: "unauthenticated" };
}

function writeStoredSession(state: SessionState) {
  try {
    if (state.status === "authenticated") {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

interface SessionContextValue {
  session: SessionState;
  // Takes an updater as well as a value: a caller reacting to an awaited request
  // holds a render-stale `session`, and writing that back would undo whatever the
  // token listener applied in the meantime.
  setSession: (next: SessionState | ((previous: SessionState) => SessionState)) => void;
}

export const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  // Starts unauthenticated on the server AND the first client render — reading
  // localStorage during the initial render diverges from SSR (no localStorage
  // there) and causes a hydration mismatch (React error #418) for anyone with
  // a stored session. The real value is synced in an effect after mount,
  // same pattern as useIsWide/useMediaQuery.
  const [session, setSessionState] = useState<SessionState>({ status: "unauthenticated" });
  // Set once another tab signs in as someone else (or signs out). The api
  // client is frozen at that point; this only drives the reload dialog.
  const [accountSwitch, setAccountSwitch] = useState<AccountSwitchInfo | null>(() => getAccountSwitch());

  useEffect(() => {
    const sync = () => {
      const stored = readStoredSession();
      if (stored.status !== "unauthenticated") {
        setSessionState(stored);
      }
    };
    sync();
  }, []);

  useEffect(() => {
    // The cookie and localStorage token are shared by every tab on the
    // origin, but each tab keeps its own in-memory token. Watch the shared
    // copy so a login/logout in another tab cannot leave this one acting as
    // one account on the client and another on the server.
    const unwatch = watchForCrossTabAccountSwitch();
    const unsubscribe = subscribeToAccountSwitch((info) => setAccountSwitch(info));
    return () => {
      unwatch();
      unsubscribe();
    };
  }, []);

  const applySessionUpdate = useCallback((updater: (prev: SessionState) => SessionState) => {
    setSessionState((previous) => {
      const next = updater(previous);
      writeStoredSession(next);
      return next;
    });
  }, []);

  const setSession = useCallback(
    (next: SessionState | ((previous: SessionState) => SessionState)) => {
      applySessionUpdate((previous) => (typeof next === "function" ? next(previous) : next));
    },
    [applySessionUpdate],
  );

  useEffect(() => {
    const unsubscribe = subscribeToAccessTokenUpdates((update: AccessTokenUpdate) => {
      if (!update.token) {
        applySessionUpdate(() => ({ status: "unauthenticated" }));
        return;
      }

      if (update.source === "refresh") {
        // The API rotated the token past half-life. Mirror it into the httpOnly
        // cookie, otherwise middleware.ts still sees the login-time expiry and
        // the next full-page navigation lands on /login. ("sync" tokens were
        // adopted from another tab that already did this.)
        void syncSessionCookie(update.token);
      }

      const payload = decodeJwtPayload(update.token);

      if (update.source === "login") {
        // Warm-start caches in localStorage are shared by every account that
        // signs in on this origin. Drop them unless the same person is signing
        // back in, so account B never starts from account A's view state.
        const previousSubject = readStoredSubject();
        const nextSubject = typeof payload?.sub === "string" ? payload.sub : null;
        if (!previousSubject || !nextSubject || previousSubject !== nextSubject) {
          clearWorkspacePreferenceCache();
        }
      }
      const expiresAt = readJwtExpiry(payload) ?? undefined;
      const issuedAt = readJwtIssuedAt(payload) ?? undefined;
      const profilePatch = deriveProfilePatch(payload);

      applySessionUpdate((previous) => {
        if (previous.status !== "authenticated") {
          return previous;
        }

        let changed = false;
        let nextSession: SessionState = previous;

        if (expiresAt && expiresAt !== previous.expiresAt) {
          nextSession = { ...nextSession, expiresAt, issuedAt };
          changed = true;
        }

        const [nextProfile, profileChanged] = applyProfilePatch(previous.profile, profilePatch);
        if (profileChanged) {
          if (nextSession === previous) {
            nextSession = { ...nextSession };
          }
          nextSession = { ...nextSession, profile: nextProfile };
          changed = true;
        }

        return changed ? nextSession : previous;
      });
    });

    return () => {
      unsubscribe();
    };
  }, [applySessionUpdate]);

  useEffect(() => {
    if (session.status !== "authenticated" || accountSwitch) {
      return;
    }

    const { expiresAt, issuedAt } = session;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = (delay: number) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(runHeartbeat, Math.max(delay, 0));
    };

    const runHeartbeat = async () => {
      if (cancelled) {
        return;
      }

      try {
        await pingSessionHeartbeat();
        if (!cancelled) {
          scheduleNext(computeHeartbeatInterval(issuedAt, expiresAt));
        }
      } catch (error) {
        if (isAccountSwitchedError(error)) {
          // Another tab took the session; the dialog is up and a reload is the
          // only way forward. No retry, no logout redirect.
          cancelled = true;
          if (timeoutId) clearTimeout(timeoutId);
          return;
        }
        if (isUnauthorizedError(error)) {
          // Preview routes intentionally run under a dev session that fails
          // upstream auth — skip the logout redirect so the page stays put.
          const onPreview =
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/preview/");
          if (onPreview) {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
            return;
          }
          cancelled = true;
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          await logoutAndRedirect();
          return;
        }

        console.error("Background session refresh failed", error);
        if (!cancelled) {
          scheduleNext(REFRESH_RETRY_INTERVAL_MS);
        }
      }
    };

    scheduleNext(computeHeartbeatInterval(issuedAt, expiresAt));

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [session.status, session.status === "authenticated" ? session.expiresAt : undefined, accountSwitch]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, setSession }),
    [session, setSession],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <AccountSwitchedDialog info={accountSwitch} />
    </SessionContext.Provider>
  );
}

function readStoredSubject(): string | null {
  const stored = readStoredSession();
  return stored.status === "authenticated" ? stored.profile.subject || null : null;
}

function computeHeartbeatInterval(issuedAt?: string, expiresAt?: string) {
  const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
  const issued = issuedAt ? Date.parse(issuedAt) : NaN;

  if (Number.isNaN(expiry) || Number.isNaN(issued) || issued >= expiry) {
    return FALLBACK_HEARTBEAT_INTERVAL_MS;
  }

  const totalLifetime = expiry - issued;
  const quarter = totalLifetime / 4;

  return Math.max(quarter, MIN_HEARTBEAT_INTERVAL_MS);
}

function deriveProfilePatch(payload: Record<string, unknown> | null): Partial<SessionProfile> {
  const patch: Partial<SessionProfile> = {};
  if (!payload) {
    return patch;
  }

  const subject = payload["sub"];
  if (typeof subject === "string") {
    patch.subject = subject;
  }

  const email = payload["email"];
  if (typeof email === "string") {
    patch.email = email;
  }

  const name = payload["name"];
  if (typeof name === "string") {
    patch.name = name;
  }

  const picture = payload["picture"];
  if (typeof picture === "string" || picture === null) {
    patch.pictureUrl = picture ?? null;
  }

  const domain = payload["domain"];
  if (typeof domain === "string" || domain === null) {
    patch.domain = domain ?? null;
  }

  const scope = payload["scope"];
  const scopes = parseScopeClaim(scope);
  if (scopes) {
    patch.scopes = scopes;
  }

  return patch;
}

function parseScopeClaim(value: unknown): string[] | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const scopes = value
    .split(" ")
    .map((scope) => scope.trim())
    .filter(Boolean);

  return scopes.length ? scopes : [];
}

function applyProfilePatch(
  profile: SessionProfile,
  patch: Partial<SessionProfile>,
): [SessionProfile, boolean] {
  let nextProfile = profile;
  let changed = false;

  const assign = <K extends keyof SessionProfile>(key: K, value: SessionProfile[K]) => {
    if (nextProfile[key] === value) {
      return;
    }
    if (nextProfile === profile) {
      nextProfile = { ...profile };
    }
    nextProfile[key] = value;
    changed = true;
  };

  if (typeof patch.subject === "string" && patch.subject.length > 0) {
    assign("subject", patch.subject);
  }
  if (typeof patch.email === "string") {
    assign("email", patch.email);
  }
  if (typeof patch.name === "string") {
    assign("name", patch.name);
  }
  if (patch.pictureUrl !== undefined) {
    assign("pictureUrl", patch.pictureUrl);
  }
  if (patch.domain !== undefined) {
    assign("domain", patch.domain);
  }
  if (patch.scopes !== undefined) {
    const currentScopes = profile.scopes ?? null;
    if (!areStringArraysEqual(currentScopes, patch.scopes)) {
      assign("scopes", patch.scopes);
    }
  }

  return [nextProfile, changed];
}

function areStringArraysEqual(a?: string[] | null, b?: string[] | null) {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("SessionProvider is missing");
  }
  return context;
}
